import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import type { components } from "@/api/types";
import { useDemo } from "@/entities/demo";
import { RobotAvatar } from "@/entities/robot";
import { ModeSwitch } from "@/features/switch-mode";
import { ROUTES } from "@/shared/config";
import { formatMoney, formatPercent, moneySign, staggerIndex } from "@/shared/lib";
import { Button, RollingNumber } from "@/shared/ui";
import { EquityChart } from "@/widgets/equity-chart";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

type ChartPeriod = components["schemas"]["ChartPeriod"];

/**
 * Главная в демо-режиме (доработки §3).
 *
 * Одна ручка `/demo` вместо пары «заработок + мои роботы» реального контура:
 * позиций не больше `demo_max_positions`, и второй запрос за тем же списком
 * был бы вторым источником одной правды. Так же устроен и бэкенд.
 *
 * Ни одно число здесь не считается: баланс, проценты, подключённый капитал
 * приходят готовыми строками — правило ТЗ №5 в демо действует ровно так же,
 * потому что цифры на экране обязаны совпадать с тем, что покажет реальный
 * счёт.
 */
export function DemoHomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<ChartPeriod>("30d");
  const { data, isPending, isError, isFetching, refetch } = useDemo(period);

  if (isPending || isError) {
    return (
      <div className="flex flex-col gap-3 px-5 py-3">
        <TopBar title={t("demo.home.title")} subtitle={t("demo.home.subtitle")} />
        {isError ? (
          <ScreenState
            tone="loss"
            icon="warningFill"
            title={t("states.error.title")}
            body={t("states.error.body")}
            action={{ label: t("states.error.action"), onClick: () => void refetch() }}
          />
        ) : (
          <ScreenSkeleton blocks={[150, 220, 112]} />
        )}
      </div>
    );
  }

  const account = data.account;

  return (
    <div className="stagger-children flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("demo.home.title")} subtitle={t("demo.home.subtitle")} />

      {/* Тот же переключатель и на том же месте, что на реальной главной:
          демо — это не другое приложение, а второй счёт, и выход обратно
          должен лежать там, где человек его оставил. */}
      <ModeSwitch variant="outline" />

      <section
        className="flex flex-col gap-1 rounded-3xl p-5"
        style={{ backgroundImage: "var(--gradient-balance)" }}
      >
        <span className="text-caption font-medium text-text-secondary">
          {t("demo.balance")}
        </span>
        <RollingNumber
          value={formatMoney(account.demo_balance, { suffix: "USDT" })}
          className="text-display font-extrabold text-text-primary"
        />
        <span className="text-caption font-normal text-text-secondary">
          {t("demo.startBalance")} {formatMoney(account.initial_balance, { suffix: "USDT" })}
        </span>
      </section>

      <EquityChart
        points={data.chart}
        total={account.earned_total}
        totalPct={account.earned_pct}
        period={data.chart_period}
        onPeriodChange={setPeriod}
        busy={isFetching}
      />

      {/* Оператор выключил режим целиком: накопленное видно, запускать нельзя.
          Показать это надо словами — иначе кнопка «Запустить» на экране робота
          просто отвечала бы отказом без объяснения. */}
      {!data.enabled && (
        <ScreenState
          tone="info"
          icon="warningFill"
          title={t("demo.disabled.title")}
          body={t("demo.disabled.body")}
        />
      )}

      {data.positions.length === 0 ? (
        <ScreenState
          tone="info"
          icon="robotFill"
          title={t("demo.empty.title")}
          body={t("demo.empty.body")}
          action={{
            label: t("demo.empty.action"),
            onClick: () => void navigate(ROUTES.robots),
          }}
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {data.positions.map((position, index) => {
            const sign = moneySign(position.earned_total);
            const tone =
              sign < 0
                ? "text-status-loss"
                : sign > 0
                  ? "text-status-profit"
                  : "text-text-secondary";

            return (
              <li key={position.id} className="stagger" style={staggerIndex(index)}>
                <Link
                  to={ROUTES.myRobot(position.id)}
                  className="flex w-full items-center justify-between gap-3 rounded-[20px] border border-border-subtle bg-background-elevated p-4"
                >
                  <div className="flex items-center gap-[9px]">
                    <RobotAvatar robot={position.robot} size={28} />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-body font-semibold text-text-primary">
                        {position.robot.name}
                      </span>
                      <span className="text-caption font-normal text-text-secondary">
                        {t("demo.invested")}{" "}
                        {formatMoney(position.amount, { suffix: "USDT" })}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <span className={`text-body font-semibold ${tone}`}>
                      {formatMoney(position.earned_total, {
                        sign: "always",
                        suffix: "USDT",
                      })}
                    </span>
                    <span className={`text-caption font-normal ${tone}`}>
                      {formatPercent(position.earned_pct, { sign: "always" })}
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <Button
        size="cta"
        variant="secondary"
        block
        onClick={() => void navigate(ROUTES.robots)}
      >
        {t("demo.home.catalog")}
      </Button>
    </div>
  );
}
